var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { arrow, autoUpdate, flip, FloatingArrow, FloatingPortal, offset, safePolygon, shift, useDelayGroup, useDelayGroupContext, useDismiss, useFloating, useFocus, useHover, useId, useInteractions, useRole, useTransitionStyles, } from '@floating-ui/react';
import { useRef, useState } from 'react';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { colors, cssVarsThemed, typographyCss } from '@creditornot/cb-ingredients';
import { createCssVars } from '@creditornot/cb-utils';
import { baseVariables } from './PopoverBase';
import { defaultDelay } from './TooltipDelayGroup';
import { canUseDOM, getDefaultGlobalPopoverMountElement } from './utils';
var cssVars = createCssVars(['background', 'color', 'boxShadow']);
var variables = cssVarsThemed(__assign(__assign({}, baseVariables), { background: { light: colors.pepper100, dark: colors.salt100 }, color: { light: colors.salt100, dark: colors.pepper100 } }));
// padding deviates from standard 4px interval on purpose
var TooltipContentDiv = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  ", "\n  box-shadow: ", ";\n  background-color: ", ";\n  border-radius: 4px;\n  padding: 6px 10px;\n  ", ";\n  color: ", ";\n  max-width: min(40ch, 90vw);\n  white-space: pre-line;\n  word-break: break-word;\n"], ["\n  ", "\n  box-shadow: ", ";\n  background-color: ", ";\n  border-radius: 4px;\n  padding: 6px 10px;\n  ", ";\n  color: ", ";\n  max-width: min(40ch, 90vw);\n  white-space: pre-line;\n  word-break: break-word;\n"])), variables, cssVars.boxShadow, cssVars.background, typographyCss.Body3, cssVars.color);
var TooltipContent = function (_a) {
    var _b, _c, _d, _e;
    var data = _a.data, props = __rest(_a, ["data"]);
    var id = useId();
    var context = data.context, getFloatingProps = data.getFloatingProps, middlewareData = data.middlewareData;
    useDelayGroup(context, { id: id });
    var _f = useDelayGroupContext(), currentId = _f.currentId, isInstantPhase = _f.isInstantPhase;
    var arrowX = (_c = (_b = middlewareData.arrow) === null || _b === void 0 ? void 0 : _b.x) !== null && _c !== void 0 ? _c : 0;
    var arrowY = (_e = (_d = middlewareData.arrow) === null || _d === void 0 ? void 0 : _d.y) !== null && _e !== void 0 ? _e : 0;
    var transformX = arrowX + ARROW_WIDTH / 2;
    var transformY = arrowY + ARROW_HEIGHT;
    var _g = useTransitionStyles(context, {
        duration: {
            open: isInstantPhase ? 0 : 120,
            close: !isInstantPhase || currentId === id ? 80 : 0,
        },
        initial: {
            transform: 'scale(0.9)',
            opacity: 0,
        },
        common: function (_a) {
            var side = _a.side;
            return ({
                transformOrigin: {
                    top: "".concat(transformX, "px calc(100% + ").concat(ARROW_HEIGHT, "px)"),
                    bottom: "".concat(transformX, "px ").concat(-ARROW_HEIGHT, "px"),
                    left: "calc(100% + ".concat(ARROW_HEIGHT, "px) ").concat(transformY, "px"),
                    right: "".concat(-ARROW_HEIGHT, "px ").concat(transformY, "px"),
                }[side],
            });
        },
    }), isMounted = _g.isMounted, transitionStyles = _g.styles;
    return isMounted ? (React.createElement("div", __assign({ ref: context.refs.setFloating, style: context.floatingStyles }, getFloatingProps()),
        React.createElement(TooltipContentDiv, __assign({ style: transitionStyles }, props)))) : null;
};
var ARROW_HEIGHT = 6;
var ARROW_WIDTH = ARROW_HEIGHT * 2;
var GAP = 2;
var Arrow = styled(FloatingArrow)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  fill: ", ";\n"], ["\n  fill: ", ";\n"])), cssVars.background);
function useTooltip(_a) {
    var _b = _a === void 0 ? {} : _a, delayProp = _b.delay, fallbackPlacements = _b.fallbackPlacements, setControlledOpen = _b.onOpenChange, controlledOpen = _b.open, _c = _b.placement, placement = _c === void 0 ? 'top' : _c, renderArrow = _b.renderArrow;
    var _d = __read(useState(false), 2), uncontrolledOpen = _d[0], setUncontrolledOpen = _d[1];
    var arrowRef = useRef(null);
    var open = controlledOpen !== null && controlledOpen !== void 0 ? controlledOpen : uncontrolledOpen;
    var setOpen = setControlledOpen !== null && setControlledOpen !== void 0 ? setControlledOpen : setUncontrolledOpen;
    var delayContext = useDelayGroupContext().delay;
    // `useDelayGroupContext` returns zero if there is no provider
    var delay = delayProp !== null && delayProp !== void 0 ? delayProp : (delayContext !== 0 ? delayContext : defaultDelay);
    var data = useFloating({
        placement: placement,
        open: open,
        onOpenChange: setOpen,
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(ARROW_HEIGHT + GAP),
            flip({ padding: 5, fallbackPlacements: fallbackPlacements }),
            shift({ padding: 5 }),
            renderArrow && arrow({ element: arrowRef }),
        ],
    });
    var context = data.context;
    var hover = useHover(context, {
        delay: delay,
        move: false,
        enabled: controlledOpen == null,
        handleClose: safePolygon(),
    });
    var focus = useFocus(context, {
        enabled: controlledOpen == null,
    });
    var dismiss = useDismiss(context);
    var role = useRole(context, { role: 'tooltip' });
    var interactions = useInteractions([hover, focus, dismiss, role]);
    return __assign(__assign({ setOpen: setOpen, arrowRef: arrowRef }, interactions), data);
}
var defaultPortalElement = getDefaultGlobalPopoverMountElement();
export var Tooltip = function (_a) {
    var children = _a.children, content = _a.content, portalElementProp = _a.portalElement, _b = _a.renderArrow, renderArrow = _b === void 0 ? true : _b, options = __rest(_a, ["children", "content", "portalElement", "renderArrow"]);
    var _c = __read(useState(false), 2), hover = _c[0], setHover = _c[1];
    var _d = __read(useState(), 2), mounted = _d[0], setMounted = _d[1];
    var data = useTooltip(__assign(__assign({}, options), { renderArrow: renderArrow }));
    var arrowRef = data.arrowRef, context = data.context, getReferenceProps = data.getReferenceProps;
    var showTitle = !hover && !context.open && typeof content === 'string';
    var portalElement = useMemo(function () {
        if (portalElementProp)
            return portalElementProp;
        if (!mounted || !canUseDOM)
            return null;
        return (data.refs.reference.current.closest('[role=dialog]') ||
            defaultPortalElement);
    }, [portalElementProp, mounted, data.refs.reference]);
    return (React.createElement(React.Fragment, null,
        React.cloneElement(children, getReferenceProps({
            onMouseEnter: function () { return setHover(true); },
            onMouseLeave: function () { return setHover(false); },
            ref: function (el) {
                context.refs.setReference(el);
                setMounted(true);
            },
            tabIndex: 0,
            title: showTitle ? content : undefined,
        })),
        React.createElement(FloatingPortal, { root: portalElement },
            React.createElement(TooltipContent, { data: data },
                renderArrow && (React.createElement(Arrow, { context: context, height: ARROW_HEIGHT, ref: arrowRef, tipRadius: 2, width: ARROW_WIDTH })),
                content))));
};
var templateObject_1, templateObject_2;
//# sourceMappingURL=Tooltip.js.map